import { Alert, Slide, Stack, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect } from "react";
import { dismissToast } from "slices/message";
import { tokensDark } from "theme";

const AUTO_HIDE_MS = {
  success: 4000,
  info: 5000,
  warning: 6000,
  error: 7000,
};

/** Map toast type → OpsBlox theme token colors */
const getToastColors = (theme, severity) => {
  const { primary, secondary, text, background, common } = theme.palette;

  switch (severity) {
    case "success":
      return {
        accent: secondary.main,
        icon: secondary.main,
        bg: background.paper,
        fg: text.primary,
        border: alpha(secondary.main, 0.35),
      };
    case "error":
      return {
        accent: tokensDark.primary1[500],
        icon: tokensDark.primary1[500],
        bg: background.paper,
        fg: text.primary,
        border: alpha(tokensDark.primary1[500], 0.35),
      };
    case "warning":
      return {
        accent: tokensDark.secondary3[500],
        icon: tokensDark.secondary3[600],
        bg: background.paper,
        fg: text.primary,
        border: alpha(tokensDark.secondary3[500], 0.4),
      };
    case "info":
    default:
      return {
        accent: primary.main,
        icon: primary.main,
        bg: background.paper,
        fg: text.primary,
        border: alpha(primary.main, 0.35),
      };
  }
};

const ToastItem = ({ toast, isLoggedIn }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const severity = toast.type || "info";
  const colors = getToastColors(theme, severity);

  useEffect(() => {
    const ms = AUTO_HIDE_MS[severity] || 5000;
    const timer = setTimeout(() => {
      dispatch(dismissToast(toast.id));
    }, ms);
    return () => clearTimeout(timer);
  }, [dispatch, toast.id, severity]);

  if (
    !isLoggedIn &&
    /missing authorization header/i.test(toast.message || "")
  ) {
    return null;
  }

  return (
    <Slide direction="down" in mountOnEnter unmountOnExit>
      <Alert
        onClose={() => dispatch(dismissToast(toast.id))}
        severity={severity}
        variant="outlined"
        elevation={0}
        sx={{
          width: "100%",
          alignItems: "center",
          pointerEvents: "auto",
          backgroundColor: colors.bg,
          color: colors.fg,
          border: `1px solid ${colors.border}`,
          borderLeft: `4px solid ${colors.accent}`,
          borderRadius: "12px",
          boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.45 : 0.12)}`,
          "& .MuiAlert-icon": {
            color: colors.icon,
          },
          "& .MuiAlert-message": {
            color: colors.fg,
            padding: "6px 0",
          },
          "& .MuiAlert-action": {
            color: colors.fg,
            paddingTop: 0,
            alignItems: "center",
          },
          "& .MuiIconButton-root": {
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              color: colors.accent,
            },
          },
        }}
      >
        {toast.message}
        {toast.longText ? (
          <span
            style={{
              display: "block",
              opacity: 0.85,
              marginTop: 4,
              fontSize: "0.85em",
              color: "inherit",
            }}
          >
            {toast.longText}
          </span>
        ) : null}
      </Alert>
    </Slide>
  );
};

/**
 * Global toast notifications for success / error / warning / info messages.
 * Colors follow OpsBlox theme tokens (primary cyan, secondary green, OpsBlox red, warning amber).
 */
const MessageArea = () => {
  const toasts = useSelector((state) => state.message.toasts) || [];
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  const visibleToasts = toasts.filter((toast) => Boolean(toast?.message));

  if (visibleToasts.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={1}
      sx={{
        position: "fixed",
        top: 72,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: (theme) => theme.zIndex.snackbar,
        width: "100%",
        maxWidth: 480,
        px: 2,
        pointerEvents: "none",
      }}
    >
      {visibleToasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} isLoggedIn={isLoggedIn} />
      ))}
    </Stack>
  );
};

export default MessageArea;
